import { NumberInput } from '@carbon/react';
import { RangeFilterProps } from '../types';
import styles from './range-filter.module.scss';

/**
 * Numeric range filter component using Carbon NumberInput.
 * Allows entering min and max values for filtering numeric columns.
 * @param {RangeFilterProps} props - Component props
 * @param {string} props.id - Unique identifier for the filter
 * @param {string} props.label - Display label for the filter
 * @param {Array} props.value - Current range [min, max]
 * @param {Function} props.onChange - Callback when range changes
 * @param {number} [props.min] - Minimum allowed value
 * @param {number} [props.max] - Maximum allowed value
 * @param {number} [props.step] - Step increment for inputs
 * @param {string} [props.minPlaceholder] - Placeholder for min input
 * @param {string} [props.maxPlaceholder] - Placeholder for max input
 * @return {JSX.Element} The range filter element
 */
export function RangeFilter({
  id,
  label,
  value,
  onChange,
  min,
  max,
  step,
  minPlaceholder = 'Min',
  maxPlaceholder = 'Max',
}: RangeFilterProps) {
  const [minValue, maxValue] = value;

  const handleMinChange = (
    _e: React.SyntheticEvent,
    { value: newValue }: { value: string | number }
  ) => {
    const parsed = newValue === '' ? null : Number(newValue);
    onChange([parsed, maxValue]);
  };

  const handleMaxChange = (
    _e: React.SyntheticEvent,
    { value: newValue }: { value: string | number }
  ) => {
    const parsed = newValue === '' ? null : Number(newValue);
    onChange([minValue, parsed]);
  };

  return (
    <div className={styles.rangeFilter}>
      <span className={styles.rangeLabel}>{label}</span>
      <div className={styles.rangeInputs}>
        <NumberInput
          id={`${id}-min`}
          label=""
          hideLabel
          value={minValue ?? ''}
          min={min}
          max={max}
          step={step}
          hideSteppers
          allowEmpty
          placeholder={minPlaceholder}
          size="md"
          onChange={handleMinChange}
        />
        <NumberInput
          id={`${id}-max`}
          label=""
          hideLabel
          value={maxValue ?? ''}
          min={min}
          max={max}
          step={step}
          hideSteppers
          allowEmpty
          placeholder={maxPlaceholder}
          size="md"
          onChange={handleMaxChange}
        />
      </div>
    </div>
  );
}
